import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import UserCardAvatar from "../UserCardAvatar";
import { formatMessagePreview } from "../../../utils/messageUtils";
import formatConversationTimestamp from "../../../utils/conversationTimestamp";
import useLocaleDateFormatter from "../../../hooks/useLocaleDateFormatter";

export default function DirectMessageListItem({ dm, isActive, onClick, preview, isTyping, timestamp }) {
    const { t } = useTranslation();
    const { locale } = useLocaleDateFormatter();
    const accessibleLabel = dm.isPersonal
        ? "Personal direct message"
        : `Direct message with ${dm.username || "user"}`;
    const previewText = useMemo(
        () => formatMessagePreview(preview),
        [preview]
    );
    const lastActivityLabel = useMemo(
        () => formatConversationTimestamp(timestamp, { locale }),
        [timestamp, locale]
    );
    const resolvedTimeLabel = lastActivityLabel
        || t("bandsPage.sidebar.direct.newBadge", { defaultValue: "New" });
    const showTyping = Boolean(isTyping);
    return (
        <li>
            <button
                type="button"
                className={classNames("band-sidebar__channel", {
                    "is-active": isActive,
                })}
                onClick={onClick}
                aria-label={accessibleLabel}
            >
                <span className="band-sidebar__avatar" aria-hidden="true">
                    <UserCardAvatar
                        userId={dm.friendUserId}
                        username={dm.username}
                        alt={dm.username || "User"}
                        size="32px"
                        radius="25%"
                        status={{ online: Boolean(dm.online), lastSeen: dm.lastSeen, size: '10px', offset: '4px', placement: 'br' }}
                    />
                </span>
                <span className="band-sidebar__channel-text">
                    <span className="band-sidebar__channel-header">
                        <span className="band-sidebar__channel-name">{dm.username}</span>
                        <span className="chat-item__time">{resolvedTimeLabel}</span>
                    </span>
                    <span
                        className="band-sidebar__channel-preview chat-item__preview"
                        data-typing={showTyping || undefined}
                    >
                        {showTyping ? (
                            <span className="chat-item__typing-indicator">
                                <span className="chat-item__typing-dot" />
                                <span className="chat-item__typing-dot" />
                                <span className="chat-item__typing-dot" />
                            </span>
                        ) : (
                            previewText
                        )}
                    </span>
                </span>
            </button>
        </li>
    );
}

DirectMessageListItem.propTypes = {
    dm: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        chatId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        friendUserId: PropTypes.string,
        username: PropTypes.string,
        online: PropTypes.bool,
        lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
        presenceStatus: PropTypes.oneOf(["online", "offline", "away", "unknown"]),
        isPersonal: PropTypes.bool,
        needsCreation: PropTypes.bool,
    }).isRequired,
    isActive: PropTypes.bool,
    onClick: PropTypes.func,
    preview: PropTypes.string,
    isTyping: PropTypes.bool,
    timestamp: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.instanceOf(Date),
    ]),
};
